import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ApiError } from '../../abstract/api-error';
import { LookupUserService } from '../../core/api-services/lookup-user.service';
import { UsersOrganizationsService } from '../../core/api-services/users-organizations.service';
import { OrganizationService, ORGANIZATION_OPTIONS_PAGE_SIZE } from '../../core/data-services/organization.service';
import { Organization } from '../../core/data-models/organization.model';
import { CreateUserOrganizationRequest } from '../../core/request-models/create-user-organization-request.model';
import { LookupUserResult } from '../../core/response-models/lookup-user-result.model';
import { existingUserFromError } from '../../core/utils/existing-user-error-utils';
import { firstValueFrom, debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { MembershipRoles } from '../../core/data-models/membership-roles.model';
import { UsStateSelect } from 'src/app/shared/components/app-us-state-select/app-us-state-select';
import { OrganizationType } from 'src/app/core/data-models/organization-type.model';
import { ORGANIZATION_TYPE_OPTIONS } from 'src/app/shared/constants/organization-type-options';

/**
 * Create User Organization Component
 *
 * Streamlined admin workflow for creating users and organizations together.
 * Features:
 * - User lookup toggle (create new vs assign existing)
 * - Combined user + organization creation in single form
 * - Bitwise role selection
 * - Email is NOT sent automatically - admin triggers manually later
 */
@Component({
  selector: 'app-create-user-organization',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, UsStateSelect],
  templateUrl: './create-user-organization.component.html',
  styleUrl: './create-user-organization.component.scss'
})
export class CreateUserOrganizationComponent implements OnInit {
  createForm: FormGroup;
  loading = false;
  loadingOrganizations = false;
  lookingUpUser = false;
  errorMessage = '';
  organizations: Organization[] = [];
  MembershipRoles = MembershipRoles;

  /** User mode: 'new' for creating new user, 'existing' for assigning existing */
  userMode: 'new' | 'existing' = 'new';

  /** Organization mode: 'new' for creating new org, 'existing' for adding to existing */
  organizationMode: 'new' | 'existing' = 'new';

  /** Found existing user from lookup */
  existingUser: LookupUserResult | null = null;

  /** Email lookup subject for debouncing */
  private emailLookup$ = new Subject<string>();

  /** Available roles for selection */
  existingOrgRoles = [
    { value: MembershipRoles.Owner, label: 'Owner', description: 'Full organization management' },
    { value: MembershipRoles.ChiefSupervisor, label: 'Chief Supervisor', description: 'Operational oversight' },
    { value: MembershipRoles.PilotInCharge, label: 'Pilot in Command', description: 'Can supervise flights' },
    { value: MembershipRoles.Pilot, label: 'Pilot', description: 'Can log flights' },
  ];

  newOrgRoles = [
    { value: MembershipRoles.Owner, label: 'Owner', description: 'Full organization management' },
  ];

  availableRoles = this.newOrgRoles;

  // Dropdown options
  organizationTypes = ORGANIZATION_TYPE_OPTIONS;

  // Organization types that require EIN (per database constraint)
  einRequiredTypes = [OrganizationType.Llc, OrganizationType.SCorp, OrganizationType.CCorp, OrganizationType.Partnership];
  // Organization types that require SSN (per database constraint)
  ssnRequiredTypes = [OrganizationType.Individual];
  // Organization types that show EIN field (optional for some)
  einVisibleTypes = [OrganizationType.Llc, OrganizationType.SCorp, OrganizationType.CCorp, OrganizationType.Partnership, OrganizationType.NonProfit];
  // Organization types that show SSN field (optional for some)
  ssnVisibleTypes = [OrganizationType.Individual, OrganizationType.SoleProprietorship];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private usersOrganizationsService: UsersOrganizationsService,
    private lookupUserService: LookupUserService,
    private organizationService: OrganizationService
  ) {
    this.createForm = this.fb.group({
      // User fields
      email: ['', [Validators.required, Validators.email]],
      firstName: [''],
      lastName: [''],
      role: [MembershipRoles.Owner, Validators.required],

      // Organization fields (for new org)
      organizationName: [''],
      organizationType: [OrganizationType.SoleProprietorship, Validators.required],
      ein: [''],
      ssn: [''],
      streetAddress: [''],
      city: [''],
      state: [''],
      zipCode: [''],

      // Existing org selection
      organizationId: [''],

      // Existing user selection
      existingProfileId: [''],
    });
  }

  ngOnInit(): void {
    this.loadOrganizations();

    // Listen for organization type changes to update EIN/SSN validation.
    this.createForm.get('organizationType')?.valueChanges.subscribe(type => {
      this.updateTaxIdValidators(type);
    });

    // Apply validators once for the initial value too.
    this.updateTaxIdValidators(this.createForm.get('organizationType')?.value);

    // Set up email lookup debouncing
    this.emailLookup$.pipe(
      debounceTime(500),
      distinctUntilChanged()
    ).subscribe(email => {
      if (email && this.userMode === 'existing') {
        this.performUserLookup(email);
      }
    });
  }

  /**
   * Set user mode and update form accordingly
   */
  setUserMode(mode: 'new' | 'existing'): void {
    this.userMode = mode;
    this.existingUser = null;
    const emailControl = this.createForm.get('email');

    //New User
    if (mode === 'new') {
      this.createForm.get('existingProfileId')?.setValue('');
      emailControl?.setValidators([Validators.required, Validators.email]);
    //Existing User
    } else {
      // Clear new user fields
      this.createForm.get('firstName')?.setValue('');
      this.createForm.get('lastName')?.setValue('');
      emailControl?.clearValidators();
      this.lookupUser();
    }
    emailControl?.updateValueAndValidity();
  }

  /**
   * Set organization mode and update form accordingly
   */
  setOrganizationMode(mode: 'new' | 'existing'): void {
    this.organizationMode = mode;

    if (mode === 'new') {
      this.createForm.get('organizationId')?.setValue('');
      this.availableRoles = this.newOrgRoles;
      this.setRole(MembershipRoles.Owner);
    } else {
      this.createForm.get('organizationName')?.setValue('');
      this.createForm.get('streetAddress')?.setValue('');
      this.createForm.get('city')?.setValue('');
      this.createForm.get('state')?.setValue('');
      this.createForm.get('zipCode')?.setValue('');
      this.availableRoles = this.existingOrgRoles;
    }
  }

  /**
   * Update EIN/SSN validators based on organization type. Mirrors the
   * pattern in OrganizationFormComponent so both creation flows enforce
   * the DB's chk_ein_ssn_based_on_type constraint client-side.
   */
  private updateTaxIdValidators(orgType: OrganizationType): void {
    const einControl = this.createForm.get('ein');
    const ssnControl = this.createForm.get('ssn');

    // Clear existing validators
    einControl?.clearValidators();
    ssnControl?.clearValidators();

    // EIN: required for business entities; pattern always applies if visible.
    if (this.einRequiredTypes.includes(orgType)) {
      einControl?.setValidators([Validators.required, Validators.pattern(/^\d{2}-?\d{7}$/)]);
    } else if (this.einVisibleTypes.includes(orgType)) {
      einControl?.setValidators([Validators.pattern(/^\d{2}-?\d{7}$/)]);
    }

    // SSN: required for individuals; pattern always applies if visible.
    if (this.ssnRequiredTypes.includes(orgType)) {
      ssnControl?.setValidators([Validators.required, Validators.pattern(/^\d{3}-?\d{2}-?\d{4}$/)]);
    } else if (this.ssnVisibleTypes.includes(orgType)) {
      ssnControl?.setValidators([Validators.pattern(/^\d{3}-?\d{2}-?\d{4}$/)]);
    }

    einControl?.updateValueAndValidity();
    ssnControl?.updateValueAndValidity();
  }

  /**
   * Handle email input changes for user lookup
   */
  onEmailChange(event: Event): void {
    const email = (event.target as HTMLInputElement).value;
    if (this.userMode === 'existing') {
      this.emailLookup$.next(email);
    }
  }

  /**
   * Manually trigger user lookup
   */
  lookupUser(): void {
    const email = this.createForm.get('email')?.value;
    if (email) {
      this.performUserLookup(email);
    }
  }

  /**
   * Perform user lookup by email
   */
  private async performUserLookup(email: string): Promise<void> {
    this.lookingUpUser = true;
    this.existingUser = null;

    try {
      const user = await firstValueFrom(this.lookupUserService.lookupUser(email));
      this.existingUser = user;
      this.createForm.get('existingProfileId')?.setValue(user.id);
    } catch (error: unknown) {
      if (ApiError.fromHttpError(error).status === 404) {
        this.existingUser = null;
        this.createForm.get('existingProfileId')?.setValue('');
      } else {
        console.error('User lookup error:', error);
      }
    } finally {
      this.lookingUpUser = false;
    }
  }

  /**
   * Use the found existing user
   */
  useExistingUser(): void {
    if (this.existingUser) {
      this.createForm.get('existingProfileId')?.setValue(this.existingUser.id);
    }
  }

  /**
   * Switch to creating a new user instead
   */
  createNewUserInstead(): void {
    this.setUserMode('new');
    this.existingUser = null;
  }

  /**
   * Load organizations from API
   */
  async loadOrganizations(): Promise<void> {
    this.loadingOrganizations = true;

    try {
      // The full list rows (not getOptions) because the owner warning needs `ownerProfileId`.
      const page = await firstValueFrom(
        this.organizationService.getOrganizations({ pageSize: ORGANIZATION_OPTIONS_PAGE_SIZE, sortBy: 'name', sortOrder: 'asc' })
      );
      this.organizations = page.items;
    } catch (error) {
      console.error('Failed to load organizations:', error);
      this.organizations = [];
    } finally {
      this.loadingOrganizations = false;
    }
  }

  /**
   * Validate form before submission
   */
  private validateForm(): string | null {
    const formData = this.createForm.value;

    // Validate email (required unless using existing user)
    if (this.userMode === 'new' && !formData.email?.trim()) {
      return 'Email is required';
    }

    if (this.userMode === 'existing' && !formData.existingProfileId) {
      return 'Please look up and select an existing user';
    }

    // Validate role
    if (!formData.role || formData.role <= 0) {
      return 'Please select a role';
    }

    // Validate organization
    if (this.organizationMode === 'new') {
      if (!formData.organizationName?.trim()) {
        return 'Organization name is required';
      }
      if (!formData.streetAddress?.trim()) {
        return 'Street address is required';
      }
      if (!formData.city?.trim()) {
        return 'City is required';
      }
      if (!formData.state) {
        return 'State is required';
      }
      if (!formData.zipCode?.trim()) {
        return 'Zip code is required';
      }
      // Validate zip format
      const zipRegex = /^\d{5}(-\d{4})?$/;
      if (!zipRegex.test(formData.zipCode)) {
        return 'Invalid zip code format. Use 12345 or 12345-6789';
      }
    } else {
      if (!formData.organizationId) {
        return 'Please select an organization';
      }
    }

    return null;
  }

  async onSubmit(): Promise<void> {
    // Reactive-form validators (required, email format, EIN/SSN pattern, etc.)
    // run first so the inline error states render correctly per field.
    if (this.createForm.invalid) {
      this.createForm.markAllAsTouched();
      this.errorMessage = 'Please fix the highlighted fields.';
      return;
    }

    // Custom cross-field / mode-aware checks not expressible as plain validators.
    const validationError = this.validateForm();
    if (validationError) {
      this.errorMessage = validationError;
      return;
    }

    // If the role is owner, confirm with the user before proceeding
    if (this.isRoleSelected(MembershipRoles.Owner) && this.selectedOrgHasOwner) {
      const confirmOverride = confirm('Selecting the Owner role will override the current owner. Do you want to proceed?');
      if (!confirmOverride) {
        return;
      }
    }

    this.loading = true;
    this.errorMessage = '';

    try {
      const formData = this.createForm.value;

      // Build request payload
      const payload: CreateUserOrganizationRequest = {
        role: formData.role,
      };

      // User fields
      if (this.userMode === 'existing' && formData.existingProfileId) {
        payload.existingProfileId = formData.existingProfileId;
      } else {
        payload.email = formData.email;
        payload.firstName = formData.firstName || undefined;
        payload.lastName = formData.lastName || undefined;
      }

      // Organization fields
      if (this.organizationMode === 'existing' && formData.organizationId) {
        payload.organizationId = formData.organizationId;
      } else {
        payload.organizationName = formData.organizationName;
        payload.organizationType = formData.organizationType;
        payload.ein = formData.ein;
        payload.ssn = formData.ssn;
        payload.streetAddress = formData.streetAddress;
        payload.city = formData.city;
        payload.state = formData.state;
        payload.zipCode = formData.zipCode;
      }

      const response = await firstValueFrom(this.usersOrganizationsService.create(payload));

      // Get names for success page
      const userName = this.userMode === 'existing' && this.existingUser
        ? `${this.existingUser.firstName || ''} ${this.existingUser.lastName || ''}`.trim() || this.existingUser.email
        : `${formData.firstName || ''} ${formData.lastName || ''}`.trim() || formData.email;

      const orgName = this.organizationMode === 'existing'
        ? this.organizations.find(o => o.id === formData.organizationId)?.name || ''
        : formData.organizationName;

      const userIsNew = this.userMode === 'new';
      const orgIsNew = this.organizationMode === 'new';

      // Navigate to success page
      this.router.navigate(['/success'], {
        state: {
          entities: [
            { type: 'user',         action: userIsNew ? 'created' : 'existing', name: userName },
            { type: 'organization', action: orgIsNew  ? 'created' : 'existing', name: orgName },
          ],
          details: {
            'User': userName,
            'Email': response.profileEmail || formData.email,
            'Organization': orgName,
            'Role': this.getRoleLabel(formData.role),
            // Invitation status only matters for newly created users.
            ...(userIsNew && {
              'Invitation Status': 'Email not sent - trigger manually when ready',
            }),
          },
          createAnotherRoute: '/users/create-organization',
          additionalActions: [
            {
              label: 'Continue Setup',
              route: `/organizations/${response.organizationId}/setup`,
            },
            {
              label: 'View Organization',
              route: `/organizations/${response.organizationId}`,
            },
            // Existing users already have accounts — no invitation to send.
            ...(userIsNew ? [{
              label: 'Send Invitation Email',
              route: `/organizations/${response.organizationId}`,
              queryParams: { tab: 'members' },
            }] : []),
            {
              label: userIsNew ? 'View Created User' : 'View User',
              route: `/users/${response.profileId}`,
            },
          ],
        }
      });
    } catch (err: unknown) {
      console.error('Create user/organization error:', err);
      const error = ApiError.fromHttpError(err);
      const existingUser = existingUserFromError(error);

      if (existingUser) {
        // User already exists (USER_ALREADY_EXISTS) - offer to assign instead
        this.existingUser = { ...existingUser, organizations: [] };
        this.errorMessage = 'A user with this email already exists. You can assign them to the organization instead.';
        this.userMode = 'existing';
        this.createForm.get('existingProfileId')?.setValue(existingUser.id);
      } else if (error.status === 409) {
        this.errorMessage = error.errors[0] ?? 'A user or organization with these details already exists';
      } else if (error.status === 400) {
        this.errorMessage = error.errors[0] ?? 'Invalid data provided';
      } else if (error.status === 404) {
        this.errorMessage = error.errors[0] ?? 'Resource not found';
      } else {
        this.errorMessage = 'Failed to create user and organization. Please try again.';
      }
    } finally {
      this.loading = false;
    }
  }

  cancel(): void {
    this.router.navigate(['/organizations']);
  }

  /**
   * Get display label for role
   */
  getRoleLabel(role: number): string {
    const labels: string[] = [];

    if (role & MembershipRoles.Owner) labels.push('Owner');
    if (role & MembershipRoles.ChiefSupervisor) labels.push('Chief Supervisor');
    if (role & MembershipRoles.PilotInCharge) labels.push('Pilot in Command');
    if (role & MembershipRoles.Pilot) labels.push('Pilot');

    return labels.join(', ') || 'None';
  }

  /**
   * Check if role is selected
   */
  isRoleSelected(roleValue: number): boolean {
    const currentRole = this.createForm.get('role')?.value || 0;
    return (currentRole & roleValue) === roleValue;
  }

  /**
   * Toggle role selection (for multi-select)
   */
  toggleRole(roleValue: number): void {
    let currentRole = this.createForm.get('role')?.value || 0;

    if ((currentRole & roleValue) === roleValue) {
      // Remove role
      currentRole = currentRole & ~roleValue;
    } else {
      // Add role
      currentRole = currentRole | roleValue;
    }

    this.createForm.get('role')?.setValue(currentRole);
  }

  /**
   * Set a single role (radio behavior)
   */
  setRole(roleValue: number): void {
    this.createForm.get('role')?.setValue(roleValue);
  }

  // Computed properties for template
  get showEinField(): boolean {
    const orgType = this.createForm.get('organizationType')?.value;
    return this.einVisibleTypes.includes(orgType);
  }

  get showSsnField(): boolean {
    const orgType = this.createForm.get('organizationType')?.value;
    return this.ssnVisibleTypes.includes(orgType);
  }

  get einRequired(): boolean {
    const orgType = this.createForm.get('organizationType')?.value;
    return this.einRequiredTypes.includes(orgType);
  }

  get ssnRequired(): boolean {
    const orgType = this.createForm.get('organizationType')?.value;
    return this.ssnRequiredTypes.includes(orgType);
  }

  get selectedOrgHasOwner(): boolean {
    const id = this.createForm.get('organizationId')?.value;
    return !!this.organizations.find(org => org.id === id)?.ownerProfileId;
  }
}
